// Maximum length of the post / project excerpt embedded in the WhatsApp message
// before it gets truncated with an ellipsis.
const EXCERPT_MAX_LENGTH = 120;

// Fallback used when no asset name is provided to the message builder. Kept in
// French because the app is French-first and the surrounding i18n labels are
// already localised by the caller.
const FALLBACK_ASSET_NAME = 'Mon bien';

export type ShareTarget =
  | { kind: 'feedPost'; postId: string }
  | { kind: 'project'; assetId: string; projectId: string };

export interface BuildWhatsAppMessageParams {
  assetName?: string | null;
  excerpt?: string | null;
  permalink: string;
  importantLabel: string;
  detailsLabel: string;
}

/**
 * Builds a fully qualified permalink for a share target, based on the current
 * window origin and the routes already declared in App.tsx.
 */
export const buildPermalink = (target: ShareTarget): string => {
  const origin = window.location.origin;

  switch (target.kind) {
    case 'feedPost':
      return `${origin}/community/post/${target.postId}`;
    case 'project':
      return `${origin}/asset/${target.assetId}/project-and-works/${target.projectId}`;
  }
};

/**
 * Truncates a string to at most EXCERPT_MAX_LENGTH characters, appending an
 * ellipsis when the input is longer.
 */
export const truncate = (value: string): string => {
  if (value.length <= EXCERPT_MAX_LENGTH) {
    return value;
  }

  return `${value.slice(0, EXCERPT_MAX_LENGTH)}…`;
};

/**
 * Builds the plain-text body shared on WhatsApp. The two i18n labels are
 * passed in by the caller so this helper stays pure and testable without any
 * react-i18next dependency.
 */
export const buildWhatsAppMessage = ({
  assetName,
  excerpt,
  permalink,
  importantLabel,
  detailsLabel,
}: BuildWhatsAppMessageParams): string => {
  const safeAssetName = assetName ?? FALLBACK_ASSET_NAME;
  const safeExcerpt = truncate(excerpt ?? '');

  return `🏠 [${safeAssetName}] ${importantLabel}\n"${safeExcerpt}"\n👉 ${detailsLabel} : ${permalink}`;
};

/**
 * Wraps a message into a wa.me URL that opens WhatsApp (web or native app
 * depending on the device) with the message pre-filled.
 */
export const buildWhatsAppShareUrl = (message: string): string =>
  `https://wa.me/?text=${encodeURIComponent(message)}`;
